import React, { useEffect, useState } from 'react'
import { Sheet } from 'components/ui/sheet'
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'
import { Switch } from 'components/ui/switch'
import { Button } from 'components/ui/button'
import { Shield } from 'lucide-react'

interface PartnerPermissionsSheetProps {
	open: boolean
	onClose: () => void
	partner: any
	isOwner: boolean
	onUpdate?: Function
}

const PartnerPermissionsSheet: React.FC<PartnerPermissionsSheetProps> = ({
	open,
	onClose,
	partner,
	isOwner,
	onUpdate,
}) => {
	const partnerData = partner?.partner || {}
	const partnerUser = partner?.partnerUserId || {}

	const [trustedPartner, setTrustedPartner] = useState(false)
	const [isSuspended, setIsSuspended] = useState(false)

	useEffect(() => {
		if (partner) {
			setTrustedPartner(partnerData.trustedPartner || false)
			setIsSuspended(partnerData.isSuspended || false)
		}
	}, [partner, partnerData.trustedPartner, partnerData.isSuspended])

	const handleUpdate = () => {
		if (onUpdate) {
			onUpdate({
				_id: partnerData._id,
				user: partnerData.user,
				partner: partnerData.partner,
				trustedPartner,
				isAccepted: partnerData.isAccepted,
				isSuspended,
				image: partnerUser.image,
				name: partnerUser.name,
				company: partnerUser.company,
				title: partnerUser.title,
				email: partnerUser.email,
			})
		}
		onClose()
	}

	return (
		<Sheet open={open} onClose={onClose} side="right" className="w-[400px] sm:w-[400px]">
			{/* Header */}
			<div className="flex items-center gap-3 px-6 py-4 border-b border-gray-200">
				<div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#0080fe]/10 text-[#0080fe] shrink-0">
					<Shield className="w-5 h-5" />
				</div>
				<div>
					<h2 className="text-lg font-semibold text-gray-900">Partner Permissions</h2>
					<p className="text-sm text-gray-500">Manage access and sharing settings</p>
				</div>
			</div>

			<div className="px-6 py-6">
				{/* Partner info with Avatar */}
				<div className="flex items-center gap-4 mb-8">
					<Avatar className="h-16 w-16 shrink-0">
						<AvatarImage src={partnerUser.image} alt={partnerUser.name} />
						<AvatarFallback className="text-xl font-semibold bg-gray-100 text-gray-600">
							{partnerUser.name ? partnerUser.name.charAt(0).toUpperCase() : '?'}
						</AvatarFallback>
					</Avatar>
					<div className="min-w-0">
						<h3 className="text-lg font-semibold text-gray-900">{partnerUser.name}</h3>
						<p className="text-sm text-gray-600">
							{partnerUser.title} - {partnerUser.company}
						</p>
						<p className="text-sm text-gray-500">{partnerUser.email}</p>
					</div>
				</div>

				{/* Permission rows */}
				<div className="space-y-6">
					{/* Full Share */}
					<div className="flex items-center justify-between gap-4">
						<div className="min-w-0">
							<p className="text-sm font-medium text-gray-900">Full Share</p>
							<p className="text-xs text-gray-500 mt-0.5">Partner has full read access to all shared documents.</p>
						</div>
						<Switch
							checked={trustedPartner}
							onChange={isOwner ? (e: React.ChangeEvent<HTMLInputElement>) => setTrustedPartner(e.target.checked) : undefined}
							disabled={!isOwner}
							className="shrink-0"
						/>
					</div>

					{/* Suspended */}
					<div className="flex items-center justify-between gap-4">
						<div className="min-w-0">
							<p className="text-sm font-medium text-gray-900">Suspended</p>
							<p className="text-xs text-gray-500 mt-0.5">
								Partner is temporarily suspended from activities related to this location.
							</p>
						</div>
						<Switch
							checked={isSuspended}
							onChange={isOwner ? (e: React.ChangeEvent<HTMLInputElement>) => setIsSuspended(e.target.checked) : undefined}
							disabled={!isOwner}
							className="shrink-0"
						/>
					</div>
				</div>

				{/* Update button */}
				{isOwner && (
					<Button onClick={handleUpdate} className="mt-8 w-full">
						Update
					</Button>
				)}
			</div>
		</Sheet>
	)
}

export default PartnerPermissionsSheet
